import React, { useCallback, useEffect, useRef, useState } from 'react';
import NavBar from '../../../components/NavBar';
import API_BASE_URL from '../../../services/config/api';

const SAFETY_HUDDLE_URL = `${String(API_BASE_URL || '').replace(
	/\/+$/,
	'',
)}/huddle/`;
const MIN_IFRAME_HEIGHT = 480;
const LOAD_TIMEOUT_MS = 20000;
const SAFETY_HUDDLE_ENABLED = true;

function SafetyHuddleUnavailablePage() {
	return (
		<div className='flex min-h-screen flex-col bg-[#F0F4F8]'>
			<NavBar />
			<main className='mt-[80px] flex flex-1 items-center justify-center px-6 text-center'>
				<div className='max-w-lg rounded-lg border border-gray-200 bg-white p-8 shadow-sm'>
					<h1 className='text-2xl font-semibold text-[#13335A]'>
						Acesso indisponível
					</h1>
					<p className='mt-3 text-gray-600'>
						O Safety Huddle está temporariamente indisponível.
					</p>
				</div>
			</main>
		</div>
	);
}

function SafetyHuddleIframePage() {
	const iframeRef = useRef(null);
	const iframeLoadedRef = useRef(false);
	const lastIframeHeightRef = useRef(MIN_IFRAME_HEIGHT);
	const [iframeHeight, setIframeHeight] = useState(MIN_IFRAME_HEIGHT);
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);

	const solicitarAtualizacaoAltura = useCallback(() => {
		iframeRef.current?.contentWindow?.postMessage(
			{ type: 'safety-huddle:request-resize' },
			new URL(SAFETY_HUDDLE_URL).origin,
		);
	}, []);

	useEffect(() => {
		const safetyHuddleOrigin = new URL(SAFETY_HUDDLE_URL).origin;
		const timeoutId = window.setTimeout(() => {
			if (!iframeLoadedRef.current) {
				setIsLoading(false);
				setHasError(true);
			}
		}, LOAD_TIMEOUT_MS);

		const handleMessage = (event) => {
			if (event.origin !== safetyHuddleOrigin) return;
			if (event.source !== iframeRef.current?.contentWindow) return;

			const data = event.data || {};
			if (
				data.source !== 'subhue' ||
				data.app !== 'huddle' ||
				data.type !== 'huddle:resize'
			)
				return;

			const reportedHeight = Number(data.height || 0);
			if (!Number.isFinite(reportedHeight) || reportedHeight <= 0) return;

			const nextHeight = Math.max(MIN_IFRAME_HEIGHT, Math.ceil(reportedHeight));
			if (lastIframeHeightRef.current === nextHeight) return;

			const iframe = iframeRef.current;
			if (!iframe) return;

			const before = window.scrollY;
			lastIframeHeightRef.current = nextHeight;
			iframe.style.height = `${nextHeight}px`;
			setIframeHeight(nextHeight);

			window.requestAnimationFrame(() => {
				console.debug('[SafetyHuddle] iframe redimensionado', {
					before,
					after: window.scrollY,
					iframeHeight: iframe.offsetHeight,
					messageHeight: nextHeight,
				});
			});
			setIsLoading(false);
			setHasError(false);
			window.clearTimeout(timeoutId);
		};

		window.addEventListener('message', handleMessage);

		return () => {
			window.clearTimeout(timeoutId);
			window.removeEventListener('message', handleMessage);
		};
	}, [solicitarAtualizacaoAltura]);

	const handleIframeLoad = () => {
		iframeLoadedRef.current = true;
		setIsLoading(false);
		setHasError(false);
		solicitarAtualizacaoAltura();
	};

	return (
		<div className='flex min-h-screen flex-col bg-[#F0F4F8]'>
			<NavBar />

			<div className='mt-[80px] bg-[#F0F4F8] px-6 py-[36px] text-center font-inter text-[26px] font-semibold text-[#13335A] sm:text-[28px]'>
				Safety Huddle
			</div>

			<main
				className='relative flex-1 bg-white'
				style={{
					overflowAnchor: 'none',
					transition: 'none',
					animation: 'none',
				}}>
				{isLoading && (
					<div className='absolute inset-0 z-20 flex min-h-[480px] items-center justify-center bg-white'>
						<div className='h-10 w-10 animate-spin rounded-full border-4 border-gray-300 border-t-gray-700' />
					</div>
				)}

				{hasError && (
					<div className='flex min-h-[480px] items-center justify-center px-6 text-center'>
						<p className='max-w-md text-[18px] font-medium text-gray-700'>
							O Safety Huddle não pôde ser carregado. Tente novamente mais tarde.
						</p>
					</div>
				)}

				{!hasError && (
					<iframe
						ref={iframeRef}
						id='safety-huddle-frame'
						src={SAFETY_HUDDLE_URL}
						title='Safety Huddle'
						onLoad={handleIframeLoad}
						style={{
							width: '100%',
							minHeight: `${MIN_IFRAME_HEIGHT}px`,
							height: `${iframeHeight}px`,
							border: 0,
							display: 'block',
							transition: 'none',
							animation: 'none',
						}}
					/>
				)}
			</main>
		</div>
	);
}

export default SAFETY_HUDDLE_ENABLED
	? SafetyHuddleIframePage
	: SafetyHuddleUnavailablePage;
